import {
  ApplicationStatus,
  DeploymentStatus,
  JobStatus,
  RequirementStatus,
  VerificationStatus,
} from '@manpower/shared';

type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/** Turns any SCREAMING_SNAKE enum into "Screaming snake" for a fallback label. */
export const humanise = (value: string): string =>
  value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ');

/**
 * Candidate-facing wording for the 19-stage funnel. Deliberately plainer than
 * the enum names — a candidate reads "Waiting for medical test", not
 * "MEDICAL_PENDING".
 */
export const APPLICATION_STATUS_LABEL: Record<ApplicationStatus, string> = {
  APPLIED: 'Applied',
  SCREENING: 'Under screening',
  SHORTLISTED: 'Shortlisted',
  INTERVIEW_SCHEDULED: 'Interview scheduled',
  INTERVIEWED: 'Interview done',
  SELECTED: 'Selected',
  MEDICAL_PENDING: 'Medical test pending',
  MEDICAL_CLEARED: 'Medical cleared',
  BGV_PENDING: 'Background check',
  BGV_CLEARED: 'Background check cleared',
  OFFER_ISSUED: 'Offer letter issued',
  OFFER_ACCEPTED: 'Offer accepted',
  OFFER_DECLINED: 'Offer declined',
  JOINED: 'Joined',
  DEPLOYED: 'Working',
  REJECTED: 'Not selected',
  WITHDRAWN: 'Withdrawn',
  NO_SHOW: 'Did not attend',
  ON_HOLD: 'On hold',
};

export const APPLICATION_STATUS_TONE: Record<ApplicationStatus, Tone> = {
  APPLIED: 'neutral',
  SCREENING: 'info',
  SHORTLISTED: 'info',
  INTERVIEW_SCHEDULED: 'info',
  INTERVIEWED: 'info',
  SELECTED: 'success',
  MEDICAL_PENDING: 'warning',
  MEDICAL_CLEARED: 'success',
  BGV_PENDING: 'warning',
  BGV_CLEARED: 'success',
  OFFER_ISSUED: 'success',
  OFFER_ACCEPTED: 'success',
  OFFER_DECLINED: 'danger',
  JOINED: 'success',
  DEPLOYED: 'success',
  REJECTED: 'danger',
  WITHDRAWN: 'neutral',
  NO_SHOW: 'danger',
  ON_HOLD: 'warning',
};

export const JOB_STATUS_TONE: Record<JobStatus, Tone> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  PUBLISHED: 'success',
  PAUSED: 'warning',
  CLOSED: 'neutral',
  REJECTED: 'danger',
};

export const VERIFICATION_TONE: Record<VerificationStatus, Tone> = {
  PENDING: 'warning',
  VERIFIED: 'success',
  REJECTED: 'danger',
  EXPIRED: 'neutral',
  UNDER_REVIEW: 'info',
  ACTION_REQUIRED: 'warning',
};

export const REQUIREMENT_TONE: Record<RequirementStatus, Tone> = {
  OPEN: 'info',
  IN_PROGRESS: 'info',
  FULFILLED: 'success',
  PARTIALLY_FULFILLED: 'warning',
  ON_HOLD: 'warning',
  CANCELLED: 'neutral',
};

export const DEPLOYMENT_TONE: Record<DeploymentStatus, Tone> = {
  JOINED: 'info',
  ACTIVE: 'success',
  ON_NOTICE: 'warning',
  TRANSFERRED: 'info',
  EXITED: 'neutral',
  ABSCONDED: 'danger',
  TERMINATED: 'danger',
  REPLACED: 'neutral',
};

/** "14 Mar 2026" — unambiguous for Indian users, unlike a numeric date. */
export const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (value: string | Date | null | undefined): string => {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** "3 days ago" / "in 2 days" — used on queues where recency is the signal. */
export const formatRelative = (value: string | Date | null | undefined): string => {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  const diffDays = Math.round((date.getTime() - Date.now()) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  return new Intl.RelativeTimeFormat('en-IN', { numeric: 'auto' }).format(diffDays, 'day');
};

/** Builds Select options from one of the shared `as const` enum objects. */
export const enumOptions = <T extends Record<string, string>>(
  source: T,
  labels?: Partial<Record<string, string>>,
): { value: string; label: string }[] =>
  Object.values(source).map((value) => ({
    value,
    label: labels?.[value] ?? humanise(value),
  }));

/**
 * Qualification, as this audience writes it.
 *
 * `humanise` would render ITI as "Iti" — an acronym title-cased into a word,
 * which reads as a typo to the exact reader who is scanning for it. The
 * trade qualifications are spelled the way they appear on the certificate.
 */
const QUALIFICATION_LABEL: Record<string, string> = {
  ITI: 'ITI',
  DIPLOMA: 'Diploma',
  BE_BTECH: 'BE / BTech',
  TENTH: '10th pass',
  TWELFTH: '12th pass',
  BELOW_TENTH: 'Below 10th',
  GRADUATE: 'Graduate',
  POST_GRADUATE: 'Post graduate',
};

export const qualificationLabel = (value: string | null | undefined): string | undefined =>
  value ? (QUALIFICATION_LABEL[value] ?? humanise(value)) : undefined;

/**
 * Experience as a band rather than a number of months.
 *
 * "48 months" is a unit nobody uses about their own working life, and a
 * fresher scanning for whether they may apply needs the zero case stated
 * outright rather than inferred from a blank.
 */
export const experienceLabel = (
  minMonths: number | null | undefined,
  maxMonths?: number | null,
): string | undefined => {
  if (minMonths === null || minMonths === undefined) return undefined;
  if (minMonths <= 0) return 'Freshers welcome';
  const years = (m: number) => (m % 12 === 0 ? `${m / 12} yr` : `${m} mo`);
  return maxMonths && maxMonths > minMonths
    ? `${years(minMonths)}–${years(maxMonths)}`
    : `${years(minMonths)}+`;
};
